import React, { useRef, useState } from "react";
import {
  Card,
  Form,
  Select,
  DatePicker,
  Checkbox,
  message,
  Row,
  Col,
  Typography,
  Descriptions,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { Booking, bookingStatusLabels, BookingStatus } from "../../types/booking";
import { AGENT_EDITABLE_BOOKING_STATUSES } from "../../config/agentPortal";
import { updateAgentBooking } from "../../services/agentPortalService";
import { getBookingTypeLabel } from "../../utils/agentBookingMetrics";
import {
  isServiceLevel,
  SERVICE_LEVEL_LABELS,
} from "../../utils/serviceLevelUtils";
import { useDebouncedSave } from "../../hooks/useDebouncedSave";
import {
  renderAgentBookingStatusLabel,
  renderAgentBookingStatusOption,
} from "./agentStatusTags";
import { AgentAutoSaveStatus } from "./AgentAutoSaveStatus";
import { AgentOptionalFeeField } from "./AgentOptionalFeeField";
import { notifyAgentSaveSuccess } from "../../utils/agentAutoSaveFeedback";
import { normalizeOptionalBookingFee } from "../../utils/agentBookingFees";

const { Title } = Typography;

interface AgentBookingOverviewProps {
  booking: Booking;
  agentId: number;
  onUpdated?: () => void | Promise<void>;
}

export const AgentBookingOverview: React.FC<AgentBookingOverviewProps> = ({
  booking,
  agentId,
  onUpdated,
}) => {
  const [form] = Form.useForm();
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const scheduleSave = useDebouncedSave(600);

  const transport = Array.isArray(booking.transport_type_info)
    ? booking.transport_type_info[0]
    : booking.transport_type_info;
  const incoterm = Array.isArray(booking.booking_incoterm_info)
    ? booking.booking_incoterm_info[0]
    : booking.booking_incoterm_info;

  React.useEffect(() => {
    form.setFieldsValue({
      status: booking.status?.replace(/-/g, "_"),
      export_clearance: booking.export_clearance || false,
      commodity_inspection: booking.commodity_inspection || false,
      pick_up_fee: booking.pick_up_fee ?? null,
      post_export_license: booking.post_export_license ?? null,
      origin_received_date: booking.origin_received_date
        ? dayjs(booking.origin_received_date)
        : null,
      confirmed_pick_up_date: booking.confirmed_pick_up_date
        ? dayjs(booking.confirmed_pick_up_date)
        : null,
    });
  }, [booking, form]);

  const persistBooking = async () => {
    if (savingRef.current) return;
    try {
      const values = await form.validateFields();
      savingRef.current = true;
      setSaving(true);
      const { error } = await updateAgentBooking(agentId, booking.id, {
        status: values.status,
        export_clearance: values.export_clearance ? true : null,
        commodity_inspection: values.commodity_inspection ? true : null,
        pick_up_fee: normalizeOptionalBookingFee(values.pick_up_fee),
        post_export_license: normalizeOptionalBookingFee(values.post_export_license),
        origin_received_date: values.origin_received_date
          ? values.origin_received_date.format("YYYY-MM-DD")
          : null,
        confirmed_pick_up_date: values.confirmed_pick_up_date
          ? values.confirmed_pick_up_date.format("YYYY-MM-DD")
          : null,
      });
      if (error) throw error;
      setLastSaved(new Date());
      notifyAgentSaveSuccess("Booking", "agent-booking-save");
      await onUpdated?.();
    } catch (err) {
      console.error(err);
      message.error("Failed to save booking");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleValuesChange = () => {
    scheduleSave("booking", persistBooking);
  };

  return (
    <Card size="small" title="Booking details">
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
        <Descriptions.Item label="ID">{booking.id}</Descriptions.Item>
        <Descriptions.Item label="Reference">
          {booking.reference || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Transport">
          {transport?.name || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="LCL/FCL">
          {getBookingTypeLabel(booking)}
        </Descriptions.Item>
        <Descriptions.Item label="Service level">
          {booking.service_level && isServiceLevel(booking.service_level)
            ? SERVICE_LEVEL_LABELS[booking.service_level]
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Incoterm">
          {incoterm?.name || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          {dayjs(booking.created_at).format("MMM D, YYYY HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item label="Goods ready">
          {booking.origin_pickup_date
            ? dayjs(booking.origin_pickup_date).format("MMM D, YYYY")
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleValuesChange}
        style={{ marginTop: 16 }}
      >
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          Status &amp; dates
        </Title>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item label="Status" name="status" style={{ marginBottom: 12 }}>
              <Select
                optionRender={renderAgentBookingStatusOption}
                labelRender={renderAgentBookingStatusLabel}
              >
                {AGENT_EDITABLE_BOOKING_STATUSES.map((s) => (
                  <Select.Option key={s} value={s}>
                    {bookingStatusLabels[s as BookingStatus] || s}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Goods received in warehouse"
              name="origin_received_date"
              style={{ marginBottom: 12 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Pickup/drop off date"
              name="confirmed_pick_up_date"
              style={{ marginBottom: 12 }}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "4px 0 12px" }} />

        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          Origin fees &amp; services
        </Title>
        <Row gutter={12}>
          <Col xs={12} sm={6}>
            <Form.Item
              label="Export clearance"
              name="export_clearance"
              valuePropName="checked"
              style={{ marginBottom: 12 }}
            >
              <Checkbox>Required</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item
              label="Commodity inspection"
              name="commodity_inspection"
              valuePropName="checked"
              style={{ marginBottom: 12 }}
            >
              <Checkbox>Required</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <AgentOptionalFeeField
              form={form}
              name="pick_up_fee"
              label="Pick up fee"
              onClear={handleValuesChange}
            />
          </Col>
          <Col xs={12} sm={6}>
            <AgentOptionalFeeField
              form={form}
              name="post_export_license"
              label="Post export license"
              onClear={handleValuesChange}
            />
          </Col>
        </Row>
      </Form>

      <AgentAutoSaveStatus saving={saving} lastSaved={lastSaved} />
    </Card>
  );
};
